import React, {
  useState,
  useEffect,
  CSSProperties,
  ComponentProps,
} from "react";
import { NumericFormatProps } from "react-number-format";
import {
  FieldError,
  useFormContext,
  useController,
  Merge,
  FieldErrorsImpl,
} from "react-hook-form";
import { inputBase, inputBaseOptionalClasses, inputError } from "./inputStyles";

export type InputProps = ComponentProps<"input"> &
  Pick<NumericFormatProps, Exclude<keyof NumericFormatProps, "type">> & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error?: FieldError | Merge<FieldError, FieldErrorsImpl<any>>;
    name: string;
    inputErrorStyles?: CSSProperties;
    overrideDefaultInputHeight?: boolean;
    innerRef?: React.LegacyRef<HTMLInputElement> | undefined;
  };

const InputBase = (inputProps: InputProps) => {
  const propsToRemove = [
    "onBlur",
    "error",
    "register",
    "className",
    "style",
    "innerRef",
    "defaultValue",
    "inputErrorStyles",
    "overrideDefaultInputHeight",
  ];
  const [isIE, setIsIE] = useState(false);
  const spreadInputProps = Object.fromEntries(
    Object.entries(inputProps).filter(([key]) => !propsToRemove.includes(key)),
  );

  const spreadStyleProps = inputProps.overrideDefaultInputHeight
    ? { ...inputProps.style }
    : {
        height: "42px",
        ...inputProps.style,
      };

  const { control } = useFormContext();
  const { field } = useController({
    name: inputProps.name,
    control,
    defaultValue: inputProps.defaultValue,
  });

  const { error } = inputProps;

  useEffect(() => {
    (async () => {
      const { default: Bowser } = await import("bowser");

      if (Bowser.msie) {
        setIsIE(true);
      }
    })();
  });

  const errorStyles = inputProps.inputErrorStyles || null;

  return (
    <input
      className={`${inputBase} ${
        inputProps.className ? inputProps.className : inputBaseOptionalClasses
      } ${error ? inputError : ""} ${isIE ? "form-input--ie" : ""}`}
      style={error ? { ...spreadStyleProps, ...errorStyles } : spreadStyleProps}
      {...field}
      {...spreadInputProps}
      onBlur={(e) => {
        const input = e.currentTarget;

        if (inputProps.onBlur) {
          inputProps.onBlur(e);
        }

        if (input.value.trim().length <= 0) return;

        field.onBlur();
      }}
      ref={inputProps.innerRef}
    />
  );
};

export default InputBase;
